package com.castly.admin.ui.components

import androidx.compose.runtime.*
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.Spacing
import com.castly.shared.dto.LabelDto
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Reusable label selector component.
 *
 * - For label editors (readOnly=true): shows their label name as text, auto-fires onLabelSelected
 * - For admins: shows a dropdown of all labels, required
 */
@Composable
fun LabelSelector(
    labels: List<LabelDto>,
    selectedLabelId: Int?,
    onLabelSelected: (Int?) -> Unit,
    readOnly: Boolean = false,
    userLabelId: Int? = null,
    labelText: String = "Label"
) {
    val colors = AppTheme.colors

    // Auto-select for label editors
    if (readOnly && userLabelId != null) {
        LaunchedEffect(userLabelId) {
            onLabelSelected(userLabelId)
        }
        val labelName = labels.find { it.id == userLabelId }?.name ?: "Your Label"
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(4.px)
            }
        }) {
            Span({
                style { fontSize(12.px); color(Color(colors.textMuted)); fontWeight("500") }
            }) { Text(labelText) }
            Span({
                style {
                    fontSize(14.px)
                    color(Color(colors.text))
                    padding(Spacing.sm)
                    backgroundColor(Color(colors.surfaceHover))
                    borderRadius(BorderRadius.sm)
                }
            }) { Text(labelName) }
        }
        return
    }

    // Dropdown for admins
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(4.px)
        }
    }) {
        Span({
            style { fontSize(12.px); color(Color(colors.textMuted)); fontWeight("500") }
        }) { Text("$labelText *") }
        Select({
            onInput { event ->
                onLabelSelected(event.value?.toIntOrNull())
            }
            style {
                padding(Spacing.sm)
                borderRadius(BorderRadius.sm)
                border(1.px, LineStyle.Solid, Color(colors.border))
                backgroundColor(Color(colors.surface))
                color(Color(colors.text))
                fontSize(14.px)
                width(100.percent)
            }
        }) {
            Option("", { if (selectedLabelId == null) selected() }) { Text("-- Select Label --") }
            labels.forEach { label ->
                Option(label.id.toString(), { if (selectedLabelId == label.id) selected() }) {
                    Text(label.name)
                }
            }
        }
    }
}
